import { Component, computed, effect, inject, input, output, signal, WritableSignal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';

import { environment } from '../../../environments/environment';
import { AdminProduct } from '../../model/admin-product';
import { ProductItem } from '../../model/product-item';
import { AdminService, adminFailure } from '../../service/admin.service';
import { PageFailure } from '../../service/page-failure';
import { AdminModalComponent } from './admin-modal.component';
import { AdminPhotosComponent } from './admin-photos.component';
import { statusLabel } from './status-labels';

/**
 * The owner's toolbar on a product page: status, the edit form, photos, and adding a colour or a
 * size to the line.
 *
 * The Mustache page edited fields in place with `contenteditable` and a hand-written PATCH; here
 * the same fields are a real form in a dialog, so the browser validates the numbers and a mistake
 * can be cancelled instead of being saved on Enter.
 */
@Component({
  selector: 'admin-product-panel',
  imports: [FormsModule, AdminModalComponent, AdminPhotosComponent],
  host: {
    style: 'display: contents'
  },
  styles: `
    .admin-panel-bar {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      align-items: center;
      padding: 12px 0;
      border-top: 1px solid #e6e6e6;
      border-bottom: 1px solid #e6e6e6;
      margin-bottom: 20px;
    }

    .admin-field {
      display: block;
      margin-bottom: 12px;
    }

    .admin-field input, .admin-field select, .admin-field textarea {
      width: 100%;
      border: 1px solid #e6e6e6;
      padding: 6px 10px;
    }

    .admin-row {
      display: flex;
      gap: 10px;
    }

    .admin-row > * {
      flex: 1;
    }
    `,
  template: `
    <div class="admin-panel-bar">
      <span class="s-text8">Адміністратор:</span>

      <select #statusSelect class="s-text8" aria-label="Статус товару" [ngModel]="product().status"
        [disabled]="!admin()" (ngModelChange)="changeStatus($event, statusSelect)">
        @for (status of admin()?.statuses ?? []; track status) {
          <option [value]="status">{{label(status)}}</option>
        }
      </select>

      <button type="button" class="s-text8 admin-edit" [disabled]="!admin()" (click)="open(editing)">
        <span class="fa fa-pencil"></span> Редагувати
      </button>
      <button type="button" class="s-text8 admin-photos" (click)="open(managingPhotos)">
        <span class="fa fa-picture-o"></span> Фотографії
      </button>
      <button type="button" class="s-text8 admin-variant" (click)="open(addingVariant)">
        <span class="fa fa-plus"></span> Колір / розмір
      </button>
      <a class="s-text8" target="_blank" rel="noopener noreferrer" [href]="adminPanelUrl">
        Адміністративна панель
      </a>
      @if (saving()) {
        <span class="s-text8">Збереження…</span>
      }
      @if (loadFailure(); as loadFailure) {
        <span class="s-text5 text-danger">{{loadFailure}}</span>
      }
      @if (!editing() && !addingVariant() && failure(); as failure) {
        <span class="s-text5 text-danger">{{failure}}</span>
      }
    </div>

    @if (editing()) {
      <admin-modal title="Редагувати товар" (closed)="close(editing)">
        <form (submit)="save($event)">
          <label class="admin-field">
            Назва
            <input name="name" required [(ngModel)]="edit.name">
          </label>
          <div class="admin-row">
            <label class="admin-field">
              Ціна
              <input name="price" type="number" step="0.01" min="0" required [(ngModel)]="edit.price!.value">
            </label>
            <label class="admin-field">
              Знижка
              <input name="discount" type="number" step="0.01" min="0" [(ngModel)]="edit.price!.discount!.value">
            </label>
          </div>
          <p class="s-text8 p-b-10">
            Ціна - це те, що заплатить покупець. На картці товару знижку показано закресленою старою ціною:
            ціна + знижка.
          </p>
          <!-- A collection has no income price or code of its own; its colours and sizes do. -->
          @if (!product().collection) {
            <div class="admin-row">
              <label class="admin-field">
                Вхідна ціна
                <input name="incomePrice" type="number" step="0.01" min="0" [(ngModel)]="edit.incomePrice">
              </label>
              <label class="admin-field">
                Код товару
                <input name="code" [(ngModel)]="edit.code">
              </label>
            </div>
          }
          <div class="admin-row">
            <label class="admin-field">
              Категорія
              <select name="categoryId" [(ngModel)]="categoryId">
                @for (category of admin()?.categories ?? []; track $index) {
                  <option [value]="category.id">{{category.name}}</option>
                }
              </select>
            </label>
            <label class="admin-field">
              Популярність
              <input name="popularity" type="number" step="1" [(ngModel)]="edit.popularity">
            </label>
          </div>
          <label class="admin-field">
            Опис
            <textarea name="text" rows="6" [(ngModel)]="edit.text"></textarea>
          </label>
          @if (failure(); as failure) {
            <p class="s-text5 text-danger p-b-10">{{failure}}</p>
          }
          <button type="submit" class="flex-c-m size9 bg1 bo-rad-23 hov1 s-text1 trans-0-4 admin-save">
            Зберегти
          </button>
        </form>
      </admin-modal>
    }

    @if (managingPhotos()) {
      <admin-modal title="Фотографії" (closed)="close(managingPhotos)">
        <admin-photos [product]="product()" (changed)="changed.emit($event)" />
      </admin-modal>
    }

    @if (addingVariant()) {
      <admin-modal title="Додати колір або розмір" (closed)="close(addingVariant)">
        <form (submit)="addVariant($event)">
          <label class="admin-field">
            Що додаємо
            <select name="kind" [(ngModel)]="variantKind">
              <option value="color">Колір</option>
              <option value="size">Розмір</option>
            </select>
          </label>
          <label class="admin-field">
            Значення
            <input name="value" list="variant-values" required [(ngModel)]="variantValue">
            <datalist id="variant-values">
              @for (value of suggestions(); track $index) {
                <option [value]="value"></option>
              }
            </datalist>
          </label>
          @if (failure(); as failure) {
            <p class="s-text5 text-danger p-b-10">{{failure}}</p>
          }
          <button type="submit" class="flex-c-m size9 bg1 bo-rad-23 hov1 s-text1 trans-0-4 admin-add-variant"
            [disabled]="creatingVariant()">
            Додати
          </button>
        </form>
      </admin-modal>
    }
    `
})
export class AdminProductPanelComponent {

  readonly product = input.required<ProductItem>();
  readonly changed = output<ProductItem>();

  private router = inject(Router);
  private service = inject(AdminService);
  private pageFailure = inject(PageFailure);

  readonly admin = signal<AdminProduct | undefined>(undefined);
  readonly editing = signal(false);
  readonly managingPhotos = signal(false);
  readonly addingVariant = signal(false);
  readonly saving = signal(false);
  readonly failure = signal<string | undefined>(undefined);
  /** Each colour or size is a new product, so a double click would add it twice. */
  readonly creatingVariant = signal(false);
  /** Stays until the product changes: it is why the status select and the edit button are off. */
  readonly loadFailure = signal<string | undefined>(undefined);
  /** The plusminus panel is served by unimarket-api, not by the storefront, so it needs its host. */
  readonly adminPanelUrl = environment.apiUrl + '/admin';

  edit: Partial<ProductItem> = {};
  categoryId?: number;
  variantKind: 'color' | 'size' = 'color';
  variantValue = '';

  /** A save hands back the same product, which is no reason to load it again. */
  private productId = computed(() => this.product().id);

  constructor() {
    effect(() => {
      const id = this.productId();
      this.editing.set(false);
      this.managingPhotos.set(false);
      this.addingVariant.set(false);
      this.admin.set(undefined);
      this.edit = {};
      this.categoryId = undefined;
      this.failure.set(undefined);
      this.loadFailure.set(undefined);
      void this.service.product(id).then(admin => {
        // The colour and size choosers move the page on before a slow answer arrives.
        if (id !== this.productId()) {
          return;
        }
        this.admin.set(admin);
        this.edit = {
          name: admin.product.name,
          code: admin.product.code,
          text: admin.product.text,
          popularity: admin.product.popularity,
          incomePrice: admin.incomePrice,
          price: { ...admin.product.price, discount: { ...admin.product.price?.discount } }
        };
        this.categoryId = admin.product.category?.id;
      }, error => {
        if (id === this.productId()) {
          this.loadFailure.set(adminFailure(error, 'Не вдалося завантажити дані товару для редагування.'));
        }
      });
    });
  }

  open(dialog: WritableSignal<boolean>) {
    this.failure.set(undefined);
    dialog.set(true);
  }

  close(dialog: WritableSignal<boolean>) {
    this.failure.set(undefined);
    dialog.set(false);
  }

  label(status: string) {
    return statusLabel(status);
  }

  suggestions() {
    const admin = this.admin();
    return (this.variantKind === 'color' ? admin?.colors : admin?.sizes) ?? [];
  }

  async changeStatus(status: string, select: HTMLSelectElement) {
    if (!await this.run(() => this.service.changeStatus(this.product().id, status))) {
      // The binding still holds the saved status, so Angular sees no change to write back.
      select.value = this.product().status ?? '';
    }
  }

  async save(event: Event) {
    event.preventDefault();
    if (await this.run(() => this.service.edit(this.product().id, this.edited()))) {
      this.editing.set(false);
    }
  }

  private edited(): Partial<ProductItem> {
    const price = this.edit.price;
    return {
      ...this.edit,
      category: this.chosenCategory(),
      // A cleared box is a zero, which is how unimarket-api is told to remove the discount.
      price: { ...price, discount: { value: price?.discount?.value || 0 } }
    };
  }

  private chosenCategory() {
    const id = Number(this.categoryId);
    return this.admin()?.categories.find(category => category.id === id);
  }

  async addVariant(event: Event) {
    event.preventDefault();
    if (this.creatingVariant()) {
      return;
    }
    this.creatingVariant.set(true);
    try {
      await this.createVariant();
    } finally {
      this.creatingVariant.set(false);
    }
  }

  private async createVariant() {
    const id = this.product().id;
    const value = this.variantValue;
    let created: ProductItem;
    this.failure.set(undefined);
    try {
      created = this.variantKind === 'color'
        ? await this.service.addColor(id, value)
        : await this.service.addSize(id, value);
    } catch (error) {
      this.failure.set(adminFailure(error, 'Не вдалося додати колір або розмір. Спробуйте ще раз.'));
      return;
    }
    this.addingVariant.set(false);
    this.variantValue = '';
    // The new colour or size is a product of its own, so the page moves to it.
    try {
      await this.router.navigateByUrl(created.uri);
    } catch {
      this.pageFailure.message.set(undefined);
      this.failure.set('Колір або розмір додано, але його сторінку не вдалося відкрити. Перезавантажте сторінку.');
    }
  }

  private async run(action: () => Promise<ProductItem>): Promise<boolean> {
    this.saving.set(true);
    this.failure.set(undefined);
    try {
      this.changed.emit(await action());
      return true;
    } catch (error) {
      this.failure.set(adminFailure(error, 'Не вдалося зберегти. Спробуйте ще раз.'));
      return false;
    } finally {
      this.saving.set(false);
    }
  }

}
